import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, ArrowRight, Sparkles, Info } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { logDev } from "@/lib/ux";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { QualityChip } from "@/components/Chips";
import DateTimePicker from "@/components/DateTimePicker";
import MeetingTeamMembers from "@/components/MeetingTeamMembers";
import DealFormFields, { missingRequired } from "@/components/DealFormFields";
import { stageColor } from "@/lib/stageColors";

const MANAGER_ROLES = ["Admin", "Sales Manager"];

function suggestFlow(lead, flows) {
  const keys = flows.map((f) => f.key);
  if (lead?.business_name || lead?.website) return keys.includes("existing_brand") ? "existing_brand" : keys[0];
  return keys.includes("not_sure") ? "not_sure" : keys[0];
}

function QField({ q, value, onChange, catOptions }) {
  const t = q.question_type;
  const base = "h-10 w-full rounded-lg border border-sky bg-white px-2.5 text-sm outline-none focus:border-loop focus:ring-2 focus:ring-loop/20";
  const opts = (q.options || []).map((o) => o.value);

  if (q.category_dependent) {
    return (
      <select data-testid={`qfield-${q.question_id}`} value={value || ""} onChange={(e) => onChange(e.target.value || null)} className={base}>
        <option value="">Select…</option>
        {catOptions.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (t === "Single Select") {
    return (
      <select data-testid={`qfield-${q.question_id}`} value={value || ""} onChange={(e) => onChange(e.target.value || null)} className={base}>
        <option value="">Select…</option>
        {opts.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (t === "Multi Select") {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div className="flex flex-wrap gap-1.5" data-testid={`qfield-${q.question_id}`}>
        {opts.map((o) => {
          const on = arr.includes(o);
          return (
            <button key={o} type="button" onClick={() => onChange(on ? arr.filter((x) => x !== o) : [...arr, o])}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${on ? "border-loop bg-loop text-white" : "border-sky bg-white text-ink/70 hover:bg-mist"}`}>
              {o}
            </button>
          );
        })}
      </div>
    );
  }
  if (t === "Yes/No") {
    return (
      <div className="flex gap-2" data-testid={`qfield-${q.question_id}`}>
        {["Yes", "No"].map((o) => (
          <button key={o} type="button" onClick={() => onChange(o)}
            className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${value === o ? "border-loop bg-loop text-white" : "border-sky bg-white text-ink/70 hover:bg-mist"}`}>
            {o}
          </button>
        ))}
      </div>
    );
  }
  if (t === "Long Text") {
    return <textarea data-testid={`qfield-${q.question_id}`} value={value || ""} onChange={(e) => onChange(e.target.value || null)} rows={2} className="w-full rounded-lg border border-sky p-2.5 text-sm outline-none focus:border-loop focus:ring-2 focus:ring-loop/20" />;
  }
  if (t === "Number" || t === "Currency") {
    return <input type="number" data-testid={`qfield-${q.question_id}`} value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} className={base} />;
  }
  if (t === "Date") {
    return <input type="date" data-testid={`qfield-${q.question_id}`} value={value || ""} onChange={(e) => onChange(e.target.value || null)} className={base} />;
  }
  return <input data-testid={`qfield-${q.question_id}`} value={value || ""} onChange={(e) => onChange(e.target.value || null)} className={base} />;
}

export default function ChangeStageModal({ open, onOpenChange, leadId, lead, presetStage, onDone }) {
  const { options, user } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState("");
  const [flowKey, setFlowKey] = useState("");
  const [answers, setAnswers] = useState({});
  const [activity, setActivity] = useState({ activity_type: "Follow-up", activity_title: "", due_at: "", priority: "High", activity_note: "", internal_team_member_ids: [] });
  const [skipActivity, setSkipActivity] = useState(false);
  const [skipReason, setSkipReason] = useState("");
  const [lostReason, setLostReason] = useState("");
  const [junkReason, setJunkReason] = useState("");
  const [deal, setDeal] = useState({ deal_value_estimate: "", deal_services: [], payment_structure: "" });
  const [dealForm, setDealForm] = useState(null);
  const [dealAnswers, setDealAnswers] = useState({});
  const [qualStep, setQualStep] = useState(0);
  const [subStage, setSubStage] = useState("");
  const [openCount, setOpenCount] = useState(0);
  const [openAction, setOpenAction] = useState("keep");
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const debounce = useRef();

  useEffect(() => {
    if (!open) return;
    setStage(presetStage || lead?.stage || "");
    setAnswers({}); setFlowKey(""); setSkipActivity(false); setSkipReason("");
    setLostReason(""); setJunkReason(""); setOpenAction("keep"); setPreview(null);
    setQualStep(0);
    setSubStage(presetStage && presetStage !== lead?.stage ? "" : (lead?.sub_stage || ""));
    setDeal({ deal_value_estimate: lead?.deal_value_estimate || "", deal_services: lead?.deal_services || [], payment_structure: lead?.payment_structure || "" });
    setDealAnswers({});
    (async () => {
      setLoading(true);
      try {
        const [{ data: cfg }, { data: acts }, dealRes] = await Promise.all([
          api.get("/automation/config"),
          api.get(`/leads/${leadId}/activities`),
          api.get("/deal-forms/active").catch(() => null),
        ]);
        setConfig(cfg.data);
        setOpenCount((acts.data || []).filter((a) => a.status === "Pending" || a.status === "Snoozed").length);
        setDealForm(dealRes?.data?.data || null);
      } catch (e) { toast.error(apiError(e)); }
      finally { setLoading(false); }
    })();
  }, [open, leadId, presetStage, lead]);

  const stageCfg = (config?.stage_config || {})[stage] || {};
  const stageName = options?.stage_meta?.[stage]?.name || stage;
  const flow = (config?.flows || []).find((f) => f.key === flowKey);

  // A lead that's already cleanly Qualified shouldn't be re-asked the qualification
  // questions on a forward move — unless it's being reactivated from Junk/Lost.
  const qualComplete = lead?.last_qualification?.status === "Qualified";
  const reactivating = ["Junk", "Lost"].includes(lead?.stage);
  const requiresQual = !!stageCfg.requires_qualification && (!qualComplete || reactivating);

  // auto-suggest flow when entering a qualification stage
  useEffect(() => {
    if (config && requiresQual && !flowKey && config.flows?.length) {
      setFlowKey(suggestFlow(lead, config.flows));
    }
    if (stageCfg.requires_activity && stageCfg.activity) {
      const a = stageCfg.activity;
      setActivity((prev) => ({ ...prev, activity_type: a.type || "Follow-up", activity_title: a.title || "", priority: a.priority || "High" }));
    }
  }, [config, stage]); // eslint-disable-line react-hooks/exhaustive-deps

  // live qualification preview
  useEffect(() => {
    if (!requiresQual || !flowKey) { setPreview(null); return; }
    clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/automation/preview", { flow_key: flowKey, answers });
        setPreview(data.data);
      } catch (e) { logDev("Qualification preview failed", e); }
    }, 400);
    return () => clearTimeout(debounce.current);
  }, [answers, flowKey, stage]); // eslint-disable-line react-hooks/exhaustive-deps

  const setAns = (qid, v) => setAnswers((a) => ({ ...a, [qid]: v }));
  const segAnswer = (() => {
    const segQ = (flow?.questions || []).find((q) => q.field_mapping === "business_segment");
    return segQ ? answers[segQ.question_id] : null;
  })();
  const catOptions = options?.business_categories?.[segAnswer] || [];
  const canSkip = (() => {
    const roles = stageCfg.activity?.skip_roles || [];
    return roles.includes("*") || roles.includes(user?.role);
  })();
  const askOpen = stageCfg.open_activity_action === "ask" && openCount > 0;
  const requiresDealForm = !!stageCfg.requires_deal_form && !lead?.deal_details && !!dealForm;

  // Conditional visibility (mirrors backend question_visible)
  const qVisible = (q) => {
    const c = q.conditional;
    if (!c || !c.question_id) return true;
    const t = answers[c.question_id];
    const op = c.op || "equals";
    if (op === "equals") return String(t) === String(c.value);
    if (op === "not_equals") return String(t) !== String(c.value);
    if (op === "contains") return Array.isArray(t) ? t.includes(c.value) : String(t || "").includes(String(c.value));
    return true;
  };
  const isAnswered = (q) => { const v = answers[q.question_id]; return !(v === null || v === undefined || v === "" || (Array.isArray(v) && v.length === 0)); };
  const visibleQs = (flow?.questions || []).slice().sort((a, b) => a.display_order - b.display_order).filter(qVisible);

  // ---- Sequential step orchestrator ----
  // Required steps are surfaced ONE AT A TIME in strict priority order:
  //   1) qualification  2) deal details  3) activity creation  → then final save.
  // Only the current phase's block renders; the stage is committed only after the
  // last phase via submit().
  const qualSteps = [...new Set(visibleQs.map((q) => q.step).filter(Boolean))];
  const dealActive = !!stageCfg.requires_deal_details || requiresDealForm;
  const subStages = options?.substages_by_stage?.[stage] || [];
  const phases = [];
  if (requiresQual) {
    if (qualSteps.length) qualSteps.forEach((s) => phases.push({ kind: "qual", step: s }));
    else phases.push({ kind: "qual", step: "__qual__" });
    phases.push({ kind: "summary" });
  }
  if (dealActive) phases.push({ kind: "deal" });
  if (stageCfg.requires_activity) phases.push({ kind: "activity" });
  if (subStages.length) phases.push({ kind: "substage" });

  const multi = phases.length > 1;
  const curIdx = phases.length ? Math.min(qualStep, phases.length - 1) : 0;
  const cur = phases.length ? phases[curIdx] : null;
  const onLastStep = !phases.length || curIdx >= phases.length - 1;

  const inQualStep = cur?.kind === "qual";
  const isSummary = cur?.kind === "summary";
  const isDealPhase = cur?.kind === "deal";
  const isActivityPhase = cur?.kind === "activity";
  const isSubStagePhase = cur?.kind === "substage";
  const showQualBlock = requiresQual && (inQualStep || isSummary);
  const stepQs = !inQualStep ? [] : (qualSteps.length ? visibleQs.filter((q) => q.step === cur.step) : visibleQs);

  const stepRequiredFilled = stepQs.every((q) => !q.required || isAnswered(q));
  const activityValid = !stageCfg.requires_activity || (skipActivity && canSkip)
    || (!!activity.due_at && new Date(activity.due_at) > new Date());
  const dealValid = !requiresDealForm || missingRequired(dealForm?.fields || [], dealAnswers).length === 0;
  const subStageValid = !subStages.length || !!subStage;

  const phaseLabel = (p) => {
    if (!p) return "";
    if (p.kind === "qual") return p.step && p.step !== "__qual__" ? p.step : "Qualification";
    if (p.kind === "summary") return "Review";
    if (p.kind === "deal") return "Deal details";
    if (p.kind === "activity") return "Create activity";
    if (p.kind === "substage") return "Sub-stage";
    return "";
  };
  // Can we advance from / submit the current phase?
  const canAdvance = !cur ? true
    : isSummary ? true
    : isActivityPhase ? activityValid
    : isDealPhase ? dealValid
    : isSubStagePhase ? subStageValid
    : stepRequiredFilled;

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { stage };
      if (requiresQual) { payload.flow_key = flowKey; payload.answers = answers; }
      if (stageCfg.requires_activity) {
        if (skipActivity && canSkip) { payload.skip_activity = true; payload.skip_reason = skipReason; }
        else payload.activity = { ...activity, due_at: activity.due_at ? new Date(activity.due_at).toISOString() : null };
      }
      if (stageCfg.requires_lost_reason) payload.lost_reason = lostReason;
      if (stageCfg.requires_junk_reason) payload.junk_reason = junkReason;
      if (stageCfg.requires_deal_details) {
        payload.deal_value_estimate = deal.deal_value_estimate ? Number(deal.deal_value_estimate) : null;
        payload.deal_services = deal.deal_services;
        payload.payment_structure = deal.payment_structure || null;
      }
      if (requiresDealForm) {
        const miss = missingRequired(dealForm.fields || [], dealAnswers);
        if (miss.length) { toast.error(`Please fill deal details: ${miss.join(", ")}`); setSubmitting(false); return; }
        payload.deal_answers = dealAnswers;
      }
      if (askOpen) payload.open_activity_action = openAction;
      if (subStages.length) payload.sub_stage = subStage || null;
      await api.post(`/leads/${leadId}/change-stage`, payload);
      toast.success(`Moved to ${stageName}`);
      onDone?.(); onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSubmitting(false); }
  };

  const toggleDealService = (s) => {
    setDeal((d) => ({ ...d, deal_services: d.deal_services.includes(s) ? d.deal_services.filter((x) => x !== s) : [...d.deal_services, s] }));
  };

  const label = "mb-1 block text-xs font-semibold text-ink/70";
  const inp = "h-10 w-full rounded-lg border border-sky bg-white px-2.5 text-sm outline-none focus:border-loop focus:ring-2 focus:ring-loop/20";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-sky bg-white sm:max-w-lg" data-testid="change-stage-modal">
        <DialogHeader>
          <DialogTitle className="font-sora text-navy">Change Stage{lead?.name ? ` · ${lead.name}` : ""}</DialogTitle>
          <DialogDescription className="sr-only">Move this lead to a new stage and complete any required qualification, activity or deal details.</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-loop" /></div>
        ) : (
          <div className="space-y-4">
            {/* Stage picker */}
            <div>
              <label className={label}>Move to stage</label>
              <select data-testid="change-stage-select" value={stage} onChange={(e) => { setStage(e.target.value); setFlowKey(""); setQualStep(0); setSubStage(""); }} className={inp}>
                {(options?.stages || []).map((s) => <option key={s} value={s}>{options?.stage_meta?.[s]?.name || s}</option>)}
              </select>
            </div>

            {/* Unified step progress — sequential, one phase at a time */}
            {multi && (
              <div data-testid="qual-step-progress">
                <div className="mb-1.5 flex items-center justify-between">
                  <span className="text-[11px] font-semibold text-loop">Step {curIdx + 1} of {phases.length} · {phaseLabel(cur)}</span>
                </div>
                <div className="flex gap-1">
                  {phases.map((_, i) => <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i <= curIdx ? "bg-loop" : "bg-sky"}`} />)}
                </div>
              </div>
            )}

            {/* Qualification */}
            {showQualBlock && (
              <div className="rounded-xl border border-sky/70 bg-mist/40 p-3">
                <p className="sr-only" data-testid="step-qualification">Step 1: Qualification</p>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <label className="flex items-center gap-1 text-xs font-semibold text-navy"><Sparkles className="h-3.5 w-3.5 text-loop" />Qualification</label>
                  <select data-testid="change-stage-flow-select" value={flowKey} onChange={(e) => { setFlowKey(e.target.value); setAnswers({}); setQualStep(0); }} className="h-8 rounded-lg border border-sky bg-white px-2 text-xs outline-none focus:border-loop">
                    {(config?.flows || []).map((f) => <option key={f.key} value={f.key}>{f.name}</option>)}
                  </select>
                </div>
                {preview && (
                  <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg bg-white px-3 py-2" data-testid="qualification-preview">
                    <span className="text-xs text-ink/60">Predicted:</span>
                    <QualityChip value={preview.quality} />
                    <span className="text-xs font-semibold text-navy">Score {preview.score}</span>
                    {preview.status !== "Qualified" && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">{preview.status}</span>}
                    {preview.reasons?.length > 0 && (
                      <span className="flex w-full items-start gap-1 text-[11px] text-ink/50"><Info className="mt-0.5 h-3 w-3 flex-shrink-0" />{preview.reasons.join(" · ")}</span>
                    )}
                  </div>
                )}
                {isSummary ? (
                  <div className="space-y-2" data-testid="qual-summary">
                    <p className="text-xs font-semibold text-navy">Review before confirming</p>
                    {visibleQs.filter(isAnswered).map((q) => (
                      <div key={q.question_id} className="flex items-start justify-between gap-3 rounded-lg bg-white px-3 py-1.5 text-sm">
                        <span className="text-xs text-ink/50">{q.question_label}</span>
                        <span className="text-right font-medium text-navy">{Array.isArray(answers[q.question_id]) ? answers[q.question_id].join(", ") : answers[q.question_id]}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {stepQs.map((q) => (
                      <div key={q.question_id}>
                        <label className={label}>{q.question_label}{q.required && <span className="text-red-400"> *</span>}</label>
                        <QField q={q} value={answers[q.question_id]} onChange={(v) => setAns(q.question_id, v)} catOptions={catOptions} />
                      </div>
                    ))}
                    {stepQs.length === 0 && <p className="text-xs text-ink/45">No questions in this step.</p>}
                  </div>
                )}
              </div>
            )}

            {/* Required activity (own step — priority 3, after qualification & deal) */}
            {stageCfg.requires_activity && isActivityPhase && (
              <div className="rounded-xl border border-sky/70 bg-mist/40 p-3" data-testid="step-activity">
                <label className="mb-2 block text-xs font-semibold text-navy">Step {multi ? curIdx + 1 : ""} · Schedule the required activity</label>
                {skipActivity && canSkip ? (
                  <div>
                    <label className={label}>Reason for skipping</label>
                    <input data-testid="skip-reason-input" value={skipReason} onChange={(e) => setSkipReason(e.target.value)} className={inp} placeholder="Why is no activity needed?" />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={label}>Type</label>
                      <select data-testid="stage-activity-type" value={activity.activity_type} onChange={(e) => setActivity((a) => ({ ...a, activity_type: e.target.value, internal_team_member_ids: e.target.value === "Meeting" ? a.internal_team_member_ids : [] }))} className={inp}>
                        {(options?.activity_types || []).map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={label}>Priority</label>
                      <select data-testid="stage-activity-priority" value={activity.priority} onChange={(e) => setActivity((a) => ({ ...a, priority: e.target.value }))} className={inp}>
                        {(options?.activity_priorities || []).map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                    <div className="col-span-2">
                      <label className={label}>{activity.activity_type === "Meeting" ? "Meeting title" : "Title"}</label>
                      <input data-testid="stage-activity-title" value={activity.activity_title} onChange={(e) => setActivity((a) => ({ ...a, activity_title: e.target.value }))} className={inp} />
                    </div>
                    {activity.activity_type === "Meeting" && (
                      <div className="col-span-2">
                        <label className={label}>Agenda / Notes</label>
                        <textarea data-testid="stage-activity-note" value={activity.activity_note} onChange={(e) => setActivity((a) => ({ ...a, activity_note: e.target.value }))} rows={2} placeholder="Add meeting agenda, context, or discussion points" className={inp} />
                      </div>
                    )}
                    <div className="col-span-2">
                      <label className={label}>{activity.activity_type === "Meeting" ? "Meeting Date & Time *" : "Due date/time *"}</label>
                      <DateTimePicker value={activity.due_at} onChange={(v) => setActivity((a) => ({ ...a, due_at: v }))} testid="stage-activity-due" />
                    </div>
                    {activity.activity_type === "Meeting" && (
                      <div className="col-span-2">
                        <MeetingTeamMembers value={activity.internal_team_member_ids || []} onChange={(ids) => setActivity((a) => ({ ...a, internal_team_member_ids: ids }))} />
                      </div>
                    )}
                  </div>
                )}
                {canSkip && (
                  <label className="mt-2 flex items-center gap-2 text-xs text-ink/60">
                    <input type="checkbox" data-testid="skip-activity-checkbox" checked={skipActivity} onChange={(e) => setSkipActivity(e.target.checked)} className="h-3.5 w-3.5" />
                    Skip activity (managers only)
                  </label>
                )}
              </div>
            )}

            {/* Lost reason */}
            {stageCfg.requires_lost_reason && (
              <div>
                <label className={label}>Lost reason *</label>
                <select data-testid="lost-reason-select" value={lostReason} onChange={(e) => setLostReason(e.target.value)} className={inp}>
                  <option value="">Why was this lost?</option>
                  {(config?.lost_reasons || []).map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            )}

            {/* Junk reason */}
            {stageCfg.requires_junk_reason && (
              <div>
                <label className={label}>Junk reason *</label>
                <select data-testid="junk-reason-select" value={junkReason} onChange={(e) => setJunkReason(e.target.value)} className={inp}>
                  <option value="">Why is this junk?</option>
                  {(config?.junk_reasons || []).map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            )}

            {/* Won deal details (deal phase — priority 2) */}
            {stageCfg.requires_deal_details && isDealPhase && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-3" data-testid="step-deal-details">
                <label className="mb-2 block text-xs font-semibold text-emerald-700">Deal details</label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={label}>Final deal value (₹) *</label>
                    <input type="number" data-testid="won-deal-value" value={deal.deal_value_estimate} onChange={(e) => setDeal((d) => ({ ...d, deal_value_estimate: e.target.value }))} className={inp} />
                  </div>
                  <div>
                    <label className={label}>Payment structure</label>
                    <select data-testid="won-payment-structure" value={deal.payment_structure} onChange={(e) => setDeal((d) => ({ ...d, payment_structure: e.target.value }))} className={inp}>
                      <option value="">Select…</option>
                      {(options?.payment_structures || []).map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                </div>
                <p className="mb-1 mt-2 text-xs font-semibold text-ink/60">Services in deal</p>
                <div className="flex flex-wrap gap-1.5">
                  {(options?.services || []).map((s) => {
                    const on = deal.deal_services.includes(s);
                    return (
                      <button key={s} type="button" onClick={() => toggleDealService(s)}
                        className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${on ? "border-emerald-500 bg-emerald-500 text-white" : "border-sky bg-white text-ink/70 hover:bg-mist"}`}>
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Versioned Deal Details form (Proposal Sent / Negotiation) — deal phase */}
            {requiresDealForm && isDealPhase && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-3" data-testid="change-stage-deal-form">
                <label className="mb-2 block text-xs font-semibold text-emerald-700">Deal details {dealForm.version ? `(Form v${dealForm.version})` : ""}</label>
                <DealFormFields fields={dealForm.fields} values={dealAnswers} onChange={(id, v) => setDealAnswers((s) => ({ ...s, [id]: v }))} idPrefix="cs-deal" />
              </div>
            )}

            {/* Sub-stage selection (own phase — last; required when the stage has sub-stages) */}
            {isSubStagePhase && (
              <div className="rounded-xl border border-sky/70 bg-mist/40 p-3" data-testid="step-substage">
                <label className="mb-1 block text-xs font-semibold text-navy">Sub-stage<span className="text-red-400"> *</span></label>
                <p className="mb-2.5 text-[11px] text-ink/50">Classify this lead within {options?.stage_meta?.[stage]?.name || stage}.</p>
                <div className="flex flex-wrap gap-1.5" data-testid="substage-options">
                  {subStages.map((s) => {
                    const on = subStage === s.key;
                    const col = stageColor(s.color);
                    return (
                      <button key={s.key} type="button" onClick={() => setSubStage(s.key)} data-testid={`substage-opt-${s.key}`}
                        className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${on ? "border-loop bg-loop text-white" : "border-sky bg-white text-ink/70 hover:bg-mist"}`}>
                        <span className={`h-2 w-2 rounded-full ${on ? "bg-white" : col.dot}`} />{s.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Open activity handling — only on the final step */}
            {askOpen && onLastStep && (
              <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-3">
                <label className="mb-2 block text-xs font-semibold text-amber-700">This lead has {openCount} open {openCount === 1 ? "activity" : "activities"}. What should happen?</label>
                <div className="space-y-1.5">
                  {[["keep", "Keep them open"], ["complete", "Mark all completed"], ["cancel", "Cancel all"]].map(([v, lbl]) => (
                    <label key={v} className="flex items-center gap-2 text-sm text-ink/70">
                      <input type="radio" name="openAction" data-testid={`open-action-${v}`} checked={openAction === v} onChange={() => setOpenAction(v)} />
                      {lbl}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {multi && !onLastStep ? (
              <div className="flex gap-2">
                <button type="button" onClick={() => setQualStep((s) => Math.max(0, s - 1))} disabled={curIdx === 0} data-testid="step-back" className="h-11 flex-1 rounded-xl border border-sky font-sora text-sm font-semibold text-ink/70 hover:bg-mist disabled:opacity-40">Back</button>
                <button type="button" onClick={() => setQualStep((s) => s + 1)} disabled={!canAdvance} data-testid="step-next" className="flex h-11 flex-[2] items-center justify-center gap-2 rounded-xl bg-loop font-sora text-sm font-semibold text-white hover:bg-loophover disabled:opacity-50">
                  Next: {phaseLabel(phases[curIdx + 1])}<ArrowRight className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                {multi && (
                  <button type="button" onClick={() => setQualStep((s) => Math.max(0, s - 1))} disabled={curIdx === 0} data-testid="step-back" className="h-11 flex-1 rounded-xl border border-sky font-sora text-sm font-semibold text-ink/70 hover:bg-mist disabled:opacity-40">Back</button>
                )}
                <button onClick={submit} disabled={submitting || !canAdvance} data-testid="change-stage-submit" className="flex h-11 flex-[2] items-center justify-center gap-2 rounded-xl bg-loop font-sora text-sm font-semibold text-white hover:bg-loophover disabled:opacity-60">
                  {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : !activityValid ? <>Set a valid future activity time</> : <>Confirm move to {stageName}<ArrowRight className="h-4 w-4" /></>}
                </button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
